'use client'
import React, { useEffect, useRef } from 'react'

import { PostHogConsentBridge } from '~/lib/analytics/posthog-consent-bridge'
import { POST_MESSAGE_TYPES } from '~/lib/constants/post-message'

interface MiniCartLayoutProps {
  children: React.ReactNode
}

declare global {
  interface Window {
    __tbEmbedBootstrapScrollInstalled?: boolean
    __tbEmbedBootstrapWheelHandler?: (event: WheelEvent) => void
    __tbEmbedScrollHydrated?: boolean
  }
}

const bootstrapScrollScript = `
  (() => {
    if (window.self === window.top || window.__tbEmbedBootstrapScrollInstalled) {
      return;
    }

    window.__tbEmbedBootstrapScrollInstalled = true;
    window.__tbEmbedScrollHydrated = false;

    const handleWheel = (event) => {
      if (window.__tbEmbedScrollHydrated) {
        return;
      }

      if (typeof event.deltaY !== 'number' || event.deltaY === 0) {
        return;
      }

      event.preventDefault();
      window.parent.postMessage(
        {
          type: '${POST_MESSAGE_TYPES.SCROLL_PARENT_BY}',
          deltaY: event.deltaY,
        },
        '*',
      );
    };

    window.__tbEmbedBootstrapWheelHandler = handleWheel;
    window.addEventListener('wheel', handleWheel, { passive: false });
  })();
`

export default function Layout({ children }: MiniCartLayoutProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)

  // locale effect
  useEffect(() => {
    // Configurar comunicación con WordPress si está en iframe
    const isInIframe = window.self !== window.top

    if (isInIframe) {
      // Detectar idioma del WordPress padre usando ancestorOrigins
      const detectAndSyncLocale = () => {
        try {
          const parentOrigin = window.location.ancestorOrigins?.[0]
          if (parentOrigin) {
            const parentUrl = new URL(parentOrigin)
            const parentPath = parentUrl.pathname

            // Detectar idioma del padre:
            // /fr/ => french, /en/ => english, sin prefijo => spanish
            let parentLocale = 'es' // default Spanish
            if (parentPath.startsWith('/fr')) {
              parentLocale = 'fr'
            }
            else if (parentPath.startsWith('/en')) {
              parentLocale = 'en'
            }

            // Obtener idioma actual del iframe
            const currentPath = window.location.pathname
            const pathParts = currentPath.split('/')
            const currentLocale = pathParts[1]

            // Si el idioma del iframe no coincide con el del padre, redirigir
            if (
              ['es', 'en', 'fr'].includes(currentLocale || '')
              && currentLocale !== parentLocale
            ) {
              pathParts[1] = parentLocale
              window.location.href = pathParts.join('/')
            }
          }
        }
        catch (error) {
          console.warn('Could not detect parent locale:', error)
        }
      }

      // Detectar idioma al cargar
      detectAndSyncLocale()

      // Enviar mensaje a WordPress indicando que el iframe está listo
      window.parent.postMessage(
        {
          type: POST_MESSAGE_TYPES.IFRAME_READY,
          data: {
            url: window.location.href,
            timestamp: Date.now(),
          },
        },
        '*',
      )

      // Escuchar mensajes desde WordPress
      const handleMessage = (event: MessageEvent) => {
        // Verificar origen si es necesario
        // if (event.origin !== 'https://todobarro.com') return;

        switch (event.data.type) {
          case POST_MESSAGE_TYPES.LANGUAGE_CHANGE:
            // WordPress envía nuevo locale via postMessage (fallback)
            detectAndSyncLocale()
            break
          default:
            break
        }
      }

      window.addEventListener('message', handleMessage)

      return () => {
        window.removeEventListener('message', handleMessage)
      }
    }
  }, [])

  // iOS effect
  useEffect(() => {
    const applyIOSFix = () => {
      document.documentElement.style.cssText = `
        overscroll-behavior: none;
        overflow: hidden;
        height: 100%;
        width: 100%;
        -webkit-overflow-scrolling: touch;
      `
      document.body.style.cssText = `
        overscroll-behavior: none;
        overflow: hidden;
        height: 100%;
        width: 100%;
        -webkit-overflow-scrolling: touch;
      `
    }

    const cleanupIOSFix = () => {
      document.documentElement.style.cssText = ''
      document.body.style.cssText = ''
    }

    applyIOSFix()
    return cleanupIOSFix
  }, [])

  // Scroll effect
  useEffect(() => {
    if (window.parent === window || !wrapperRef.current) {
      return
    }

    window.__tbEmbedScrollHydrated = true

    if (window.__tbEmbedBootstrapWheelHandler) {
      window.removeEventListener('wheel', window.__tbEmbedBootstrapWheelHandler)
      delete window.__tbEmbedBootstrapWheelHandler
    }

    const isScrollable = (element: HTMLElement, deltaY: number) => {
      const hasScrollableContent = element.scrollHeight > element.clientHeight
      const overflowY = window.getComputedStyle(element).overflowY
      const canScrollY
        = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'

      if (!hasScrollableContent || !canScrollY) {
        return false
      }

      const isScrollingDown = deltaY > 0
      const isScrollingUp = deltaY < 0
      const canScrollDown
        = element.scrollTop + element.clientHeight < element.scrollHeight - 1
      const canScrollUp = element.scrollTop > 0

      return (isScrollingDown && canScrollDown) || (isScrollingUp && canScrollUp)
    }

    const shouldKeepScrollInside = (target: EventTarget | null, deltaY: number) => {
      let currentElement
        = target instanceof HTMLElement ? target : null

      while (currentElement && currentElement !== document.body) {
        if (isScrollable(currentElement, deltaY)) {
          return true
        }

        if (
          currentElement.hasAttribute('data-scroll-lock-ignore')
          && isScrollable(currentElement, deltaY)
        ) {
          return true
        }

        currentElement = currentElement.parentElement
      }

      return false
    }

    const handleWheel = (event: WheelEvent) => {
      if (shouldKeepScrollInside(event.target, event.deltaY)) {
        return
      }

      event.preventDefault()
      window.parent.postMessage(
        {
          type: POST_MESSAGE_TYPES.SCROLL_PARENT_BY,
          deltaY: event.deltaY,
        },
        '*',
      )
    }

    window.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      window.removeEventListener('wheel', handleWheel)
      window.__tbEmbedScrollHydrated = false
    }
  }, [])

  return (
    <>
      <PostHogConsentBridge />
      <script dangerouslySetInnerHTML={{ __html: bootstrapScrollScript }} />
      <div className="wrapper-ref w-full overflow-hidden bg-white p-0" ref={wrapperRef}>
        {children}
      </div>
    </>
  )
}
